import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Select from 'react-select';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

const CreateHutModal = ({ onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        date: '',
        time: '19:00',
        maxPlayers: 4,
        location: null, // Changed to object for react-select or string if simple
        minPti: -10,
        maxPti: 100
    });
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'locations'));
                let locs = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    const name = data.name || data.ClubName || 'Unknown Club';
                    return {
                        label: name,
                        value: { id: doc.id, ...data, name: name } // Ensure name is present in value
                    };
                });

                if (locs.length === 0) {
                    // Seed if empty
                    const seedLocations = [
                        { name: "Grand Park", lat: 40.045, lng: -86.008, geohash: "dp4fa" }, // Pseudo-coords
                        { name: "Westfield Center", lat: 40.0, lng: -86.1, geohash: "dp4f" },
                        { name: "City Courts", lat: 39.9, lng: -86.2, geohash: "dp4e" },
                        { name: "Northside Park", lat: 39.8, lng: -86.1, geohash: "dp4d" }
                    ];
                    const addedLocs = [];
                    for (const loc of seedLocations) {
                        const docRef = await addDoc(collection(db, 'locations'), loc);
                        addedLocs.push({ label: loc.name, value: { ...loc, id: docRef.id } });
                    }
                    locs = addedLocs;
                }
                setLocations(locs);
            } catch (error) {
                console.error("Error fetching locations:", error);
                // Fallback
                setLocations([
                    { label: "Grand Park", value: { id: "fallback-1", name: "Grand Park" } },
                    { label: "Westfield Center", value: { id: "fallback-2", name: "Westfield Center" } }
                ]);
            } finally {
                setLoadingLocations(false);
            }
        };
        fetchLocations();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.date || !formData.time || !formData.location) {
            alert("Please fill in all required fields (including Location)");
            return;
        }

        setIsSubmitting(true);

        const locationData = formData.location.value; // Now contains full object { id, name, lat, lng, ... }

        const dataToSubmit = {
            ...formData,
            location: locationData.name !== undefined ? locationData.name : null, // Keep string for backward compatibility/display
            locationId: locationData.id !== undefined ? locationData.id : null,
            locationLat: locationData.lat !== undefined ? locationData.lat : null,
            locationLng: locationData.lng !== undefined ? locationData.lng : null,
            locationGeohash: locationData.geohash !== undefined ? locationData.geohash : null
        };

        // Firestore does not allow undefined values, so we delete them
        Object.keys(dataToSubmit).forEach(key => {
            if (dataToSubmit[key] === undefined) {
                delete dataToSubmit[key];
            }
        });

        try {
            await onSubmit(dataToSubmit);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-semibold mb-4">Light a Fire (Create Hut)</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                        <input
                            type="text"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. Tuesday Night Scramble"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                            rows="3"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Competitive but friendly..."
                        />
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                            <input
                                type="date"
                                required
                                min={new Date().toLocaleDateString('en-CA')} // Prevent past dates, format to YYYY-MM-DD local time
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                            <input
                                type="time"
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                                value={formData.time}
                                onChange={e => setFormData({ ...formData, time: e.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                        <Select
                            options={locations}
                            isLoading={loadingLocations}
                            value={formData.location}
                            onChange={selectedOption => setFormData({ ...formData, location: selectedOption })}
                            placeholder="Select a location..."
                            required
                        />
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
                            <input
                                type="number"
                                min="2"
                                max="32"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                                value={formData.maxPlayers}
                                onChange={e => setFormData({ ...formData, maxPlayers: parseInt(e.target.value) })}
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">PTI Range: {formData.minPti} - {formData.maxPti}</label>
                            <div className="px-2">
                                <Slider
                                    range
                                    min={-20}
                                    max={100}
                                    step={1}
                                    value={[formData.minPti, formData.maxPti]}
                                    onChange={(val) => setFormData({ ...formData, minPti: val[0], maxPti: val[1] })}
                                    trackStyle={[{ backgroundColor: '#0284c7' }]}
                                    handleStyle={[
                                        { borderColor: '#0284c7', backgroundColor: '#fff' },
                                        { borderColor: '#0284c7', backgroundColor: '#fff' }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex justify-center items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Submitting...
                                </>
                            ) : (
                                "Light a Fire"
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CreateHutModal;
